/**
 * Structure hostiles, round 3 (vanilla Java numbers):
 *  - shulker: 30 HP, sits still, 20 armour while shut; opens on a player in
 *    sight within 16 and fires a homing bullet every 1-5.5 s; teleports when
 *    hurt below half health (1 in 4). Shell 50%.
 *  - shulker bullet: 4 damage + Levitation for 10 s; can be knocked out of
 *    the air (1 HP); dies on a block.
 *  - evoker: 24 HP, keeps its distance, casts fangs (a line of 16, or two
 *    rings when the target is within 3 blocks; 40-tick cast, 100-tick
 *    cooldown) and summons three vexes (100-tick cast, 340-tick cooldown,
 *    not while 8 are about). Drops a totem. 10 XP.
 *  - evoker fangs: snap shut after their warm-up for 6 magic damage.
 *  - vex: 14 HP, flies through blocks, 9 damage charges, starts dying 30-119 s
 *    after it was summoned.
 *  - ravager: 100 HP, 12 damage bite, 75% knockback resistance, tramples
 *    leaves and crops, roars (6 damage within 4 blocks) when hit. Saddle. 20 XP.
 *  - zoglin: 40 HP, attacks players and every mob but creepers and zoglins,
 *    3-8 damage with a toss. Rotten flesh.
 *  - piglin brute: 50 HP, golden axe, 13 damage, never distracted by gold.
 */
import { AIR, BLOCKS, IS_SOLID } from '../../blocks';
import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Mob, type MobCtx, type MobSpec } from '../mob';
import { netherHooks } from '../nether/mobs';
import { overworld2Hooks } from '../overworld2/mobs';
import { mobs3Hooks, nearestMob, ri } from './hooks';

/** Player-hunting base: sight with a short memory, chase and bite. */
abstract class Foe extends Mob {
  protected attackCd = 0;
  protected seeMem = 0;
  constructor(id: number, type: MobType, x: number, y: number, z: number, spec: MobSpec, rnd: () => number) { super(id, type, x, y, z, spec, rnd); }
  xpValue(): number { return 5; }
  protected d2(ctx: MobCtx): number {
    const p = ctx.host.player;
    return (p.x - this.x) ** 2 + (p.y - this.y) ** 2 + (p.z - this.z) ** 2;
  }
  /** Player within `range`, seen now or within the last 3 s. */
  protected sees(ctx: MobCtx, range: number): boolean {
    if (!ctx.targetable() || this.d2(ctx) > range * range) { this.seeMem = 0; return false; }
    const p = ctx.host.player;
    if ((this.phase(ctx, 5) || this.seeMem === 0) && ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z)) this.seeMem = 60;
    if (this.retaliate > 0) this.seeMem = Math.max(this.seeMem, 1);
    if (this.seeMem > 0) { this.seeMem--; return true; }
    return false;
  }
  /** Walk at the player and hit when in reach; true on the tick the hit lands. */
  protected chaseHit(ctx: MobCtx, dmg: number, mod: number, kb: number): boolean {
    const p = ctx.host.player;
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    if (this.inMeleeReach(ctx)) {
      if (this.attackCd === 0) { this.attackCd = 20; this.swingTicks = 1; ctx.hurtPlayer(this, dmg, kb); return true; }
    } else this.steerTo(ctx, p.x, p.y, p.z, mod, 3, false);
    return false;
  }
}

// ----------------------------------------------------------------- shulker

export class Shulker extends Foe {
  /** The pose rides in the replicated record so clients draw it (server mode). */
  saveExtra(): unknown { return [this.color]; }
  loadExtra(d: unknown): void { if (Array.isArray(d)) this.color = Number(d[0]) | 0; }
  /** 0 shut, 1 peeking, 2 open. */
  peek = 0;
  private peekT = 0;
  private shootCd = 40;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'shulker', x, y, z, { width: 1, height: 1, eye: 0.5, health: 30, speed: 0 }, rnd);
    this.knockbackResist = 1;
    this.persistent = true;
    this.noFallDamage = true;
  }
  /** Shut: 20 armour points (80% off, vanilla's cap); arrows bounce in vanilla, here they are just as weak. */
  override hurt(amount: number): boolean {
    return super.hurt(this.peek === 0 ? amount * 0.2 : amount);
  }
  override onHurtBy(_fx: number, _fz: number, rnd: () => number): void {
    if (this.health < this.maxHealth / 2 && rnd() < 0.25) this.teleport(rnd);
    this.retaliate = 100;
  }
  /** A free cell on a solid block within 8 blocks. */
  teleport(rnd: () => number): boolean {
    for (let i = 0; i < 12; i++) {
      const x = Math.floor(this.x) + ri(rnd, -8, 8), z = Math.floor(this.z) + ri(rnd, -8, 8);
      // vanilla picks a random cell and wants a face to attach to; here: the first floor in the column near a random height
      let y = Math.floor(this.y) + ri(rnd, -8, 8);
      for (let n = 0; n < 9 && !(y >= 1 && this.w.getBlock(x, y, z) === AIR && IS_SOLID[this.w.getBlock(x, y - 1, z)] === 1); n++) y += n % 2 === 0 ? -(n + 1) : n + 1;
      if (y < 1 || this.w.getBlock(x, y, z) !== AIR || IS_SOLID[this.w.getBlock(x, y - 1, z)] !== 1) continue;
      if (x === Math.floor(this.x) && z === Math.floor(this.z) && y === Math.floor(this.y)) continue;
      if (mobs3Hooks.mobs().some((m) => m !== this && m.type === 'shulker' && Math.floor(m.x) === x && Math.floor(m.y) === y && Math.floor(m.z) === z)) continue;
      this.host?.effect('poof', this.x, this.y + 0.5, this.z);
      this.x = x + 0.5; this.y = y; this.z = z + 0.5;
      this.vx = this.vy = this.vz = 0;
      this.peek = 0;
      return true;
    }
    return false;
  }
  protected ai(ctx: MobCtx): void {
    const r = ctx.host.rnd, p = ctx.host.player;
    this.vx = 0; this.vz = 0;
    if (this.sees(ctx, 16)) {
      this.peek = 2;
      this.lookAt(p.x, p.eyeY, p.z);
      this.aggressive = true;
      if (--this.shootCd <= 0) {
        this.shootCd = 20 + ri(r, 0, 9) * 10;
        const b = mobs3Hooks.spawn('shulker_bullet', this.x, this.y + 0.5, this.z);
        if (b instanceof ShulkerBullet) b.ownerId = this.id;
        this.swingTicks = 1;
      }
    } else {
      this.shootCd = Math.max(this.shootCd, 20);
      // idle: peeks out now and then (ShulkerPeekGoal)
      if (this.peekT > 0) { if (--this.peekT === 0) this.peek = 0; } else if (r() < 1 / 40 / 4) { this.peek = 1; this.peekT = 20 * ri(r, 1, 3); } else this.peek = 0;
    }
    this.color = this.peek;
  }
  loot(rnd: () => number): Stack[] { return rnd() < 0.5 ? [stackOf(I.SHULKER_SHELL, 1)] : []; }
}

export const BULLET_SPEED = 0.15;
export const LEVITATION_TICKS = 200;

export class ShulkerBullet extends Mob {
  ownerId = 0;
  private life = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'shulker_bullet', x, y, z, { width: 0.3125, height: 0.3125, eye: 0.15, health: 1, speed: 0 }, rnd);
    this.noFallDamage = true;
    this.persistent = true;
  }
  protected override floats(): boolean { return false; }
  private pop(): void { this.host?.effect('poof', this.x, this.y, this.z); this.removed = true; }
  protected ai(ctx: MobCtx): void {
    const p = ctx.host.player;
    if (++this.life > 600 || !ctx.targetable()) { this.pop(); return; }
    const tx = p.x, ty = p.y + p.height * 0.5, tz = p.z;
    const dx = tx - this.x, dy = ty - this.y, dz = tz - this.z;
    const d = Math.hypot(dx, dy, dz);
    if (d < 0.3125 + p.width / 2 + 0.3 || (Math.abs(dx) < p.width / 2 + 0.3 && Math.abs(dz) < p.width / 2 + 0.3 && this.y > p.y - 0.4 && this.y < p.y + p.height + 0.1)) {
      ctx.hurtPlayer(this, 4, 0);
      overworld2Hooks.hitEffect?.('levitation', LEVITATION_TICKS, 0);
      this.pop();
      return;
    }
    // vanilla bullets travel along one axis at a time; this one steers on the dominant axis
    const ax = Math.abs(dx), ay = Math.abs(dy), az = Math.abs(dz);
    let sx = dx / d, sy = dy / d, sz = dz / d;
    if (d > 3) {
      if (ax >= ay && ax >= az) { sx = Math.sign(dx); sy *= 0.3; sz *= 0.3; } else if (az >= ay) { sz = Math.sign(dz); sx *= 0.3; sy *= 0.3; } else { sy = Math.sign(dy); sx *= 0.3; sz *= 0.3; }
      const n = Math.hypot(sx, sy, sz); sx /= n; sy /= n; sz /= n;
    }
    // flies by itself (no gravity, no step-up); a solid block in the way ends it
    const nx = this.x + sx * BULLET_SPEED, ny = this.y + sy * BULLET_SPEED, nz = this.z + sz * BULLET_SPEED;
    if (IS_SOLID[this.w.getBlock(Math.floor(nx), Math.floor(ny + 0.15), Math.floor(nz))] === 1) { this.pop(); return; }
    this.x = nx; this.y = ny; this.z = nz;
    this.vx = 0; this.vy = 0; this.vz = 0;
    this.onGround = false; this.fallDistance = 0;
  }
  override tick(ctx: MobCtx): void {
    super.tick(ctx);
    if (!this.dying) { this.vx = 0; this.vy = 0; this.vz = 0; }
  }
  xpValue(): number { return 0; }
  loot(): Stack[] { return []; }
}

// ------------------------------------------------------------------ evoker

export const FANG_DAMAGE = 6;

export class EvokerFangs extends Mob {
  /** Ticks before the jaws come up. */
  warmup = 0;
  private t = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'evoker_fangs', x, y, z, { width: 0.5, height: 0.8, eye: 0.4, health: 1, speed: 0 }, rnd);
    this.knockbackResist = 1;
    this.persistent = true;
    this.noFallDamage = true;
    this.color = 0;
  }
  override hurt(): boolean { return false; }
  protected ai(ctx: MobCtx): void {
    this.vx = 0; this.vz = 0;
    if (this.warmup > 0) { this.warmup--; this.color = 0; return; }
    this.t++;
    this.color = 1;
    this.swell = Math.min(1, this.t / 22);
    if (this.t === 8) {
      const p = ctx.host.player;
      if (ctx.targetable() && Math.abs(p.x - this.x) < 0.25 + p.width / 2 + 0.2 && Math.abs(p.z - this.z) < 0.25 + p.width / 2 + 0.2 && p.y < this.y + 0.8 && p.y + p.height > this.y) ctx.hurtPlayer(this, FANG_DAMAGE, 0);
      for (const m of mobs3Hooks.mobs()) {
        if (m.type === 'villager' || m.type === 'iron_golem') {
          if (Math.abs(m.x - this.x) < 0.25 + m.width / 2 && Math.abs(m.z - this.z) < 0.25 + m.width / 2 && Math.abs(m.y - this.y) < 1) mobs3Hooks.hurtMob(m, FANG_DAMAGE, this, 0);
        }
      }
    }
    if (this.t >= 22) this.removed = true;
  }
  xpValue(): number { return 0; }
  loot(): Stack[] { return []; }
}

export const FANG_CAST = 40;
export const FANG_COOLDOWN = 100;
export const VEX_CAST = 100;
export const VEX_COOLDOWN = 340;

export class Evoker extends Foe {
  /** The pose rides in the replicated record so clients draw it (server mode). */
  saveExtra(): unknown { return [this.color]; }
  loadExtra(d: unknown): void { if (Array.isArray(d)) this.color = Number(d[0]) | 0; }
  /** 0 none, 1 fangs, 2 summon. */
  spell = 0;
  castT = 0;
  fangCd = 20;
  vexCd = 60;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'evoker', x, y, z, { width: 0.6, height: 1.95, eye: 1.62, health: 24, speed: 0.5 }, rnd);
  }
  xpValue(): number { return 10; }

  private floorY(x: number, z: number, y0: number): number {
    for (let y = Math.floor(y0) + 1; y >= Math.floor(y0) - 3; y--) {
      if (IS_SOLID[this.w.getBlock(Math.floor(x), y - 1, Math.floor(z))] === 1 && IS_SOLID[this.w.getBlock(Math.floor(x), y, Math.floor(z))] !== 1) return y;
    }
    return NaN;
  }
  private fang(x: number, z: number, y0: number, delay: number): void {
    const y = this.floorY(x, z, y0);
    if (Number.isNaN(y)) return;
    const f = mobs3Hooks.spawn('evoker_fangs', x, y, z);
    if (f instanceof EvokerFangs) { f.warmup = delay; f.yaw = this.yaw; }
  }
  /** Vanilla EvokerAttackSpellGoal.performSpellCasting. */
  castFangs(tx: number, ty: number, tz: number): void {
    const a = Math.atan2(tz - this.z, tx - this.x);
    const y0 = Math.min(ty, this.y);
    if ((tx - this.x) ** 2 + (tz - this.z) ** 2 < 9) {
      for (let i = 0; i < 5; i++) { const b = a + i * Math.PI * 0.4; this.fang(this.x + Math.cos(b) * 1.5, this.z + Math.sin(b) * 1.5, y0, 0); }
      for (let i = 0; i < 8; i++) { const b = a + i * Math.PI * 2 / 8 + 1.2566371; this.fang(this.x + Math.cos(b) * 2.5, this.z + Math.sin(b) * 2.5, y0, 3); }
    } else {
      for (let i = 0; i < 16; i++) { const d = 1.25 * (i + 1); this.fang(this.x + Math.cos(a) * d, this.z + Math.sin(a) * d, y0, i); }
    }
  }
  summonVexes(rnd: () => number): void {
    for (let i = 0; i < 3; i++) {
      const v = mobs3Hooks.spawn('vex', this.x + ri(rnd, -2, 2), this.y + 1 + rnd() * 2, this.z + ri(rnd, -2, 2));
      if (v instanceof Vex) { v.lifeTicks = 20 * (30 + ri(rnd, 0, 89)); v.ownerId = this.id; }
    }
  }

  protected ai(ctx: MobCtx): void {
    const r = ctx.host.rnd, p = ctx.host.player;
    if (this.fangCd > 0) this.fangCd--;
    if (this.vexCd > 0) this.vexCd--;
    this.color = this.spell;
    if (!this.sees(ctx, 16)) {
      this.spell = 0; this.castT = 0;
      this.stroll(ctx, 0.6, 3, false);
      this.lookAtNearPlayer(ctx);
      return;
    }
    this.lookAt(p.x, p.eyeY, p.z);
    if (this.spell !== 0) {
      // casting: stands still, arms up
      this.aggressive = true;
      this.faceToward(p.x, p.z);
      if (--this.castT <= 0) {
        if (this.spell === 1) { this.castFangs(p.x, p.y, p.z); this.fangCd = FANG_COOLDOWN; } else { this.summonVexes(r); this.vexCd = VEX_COOLDOWN; }
        this.spell = 0;
      }
      return;
    }
    if (this.vexCd === 0 && this.phase(ctx, 5)) {
      let n = 0;
      for (const m of mobs3Hooks.mobs()) if (m.type === 'vex' && m.alive && (m.x - this.x) ** 2 + (m.z - this.z) ** 2 < 16 * 16) n++;
      if (n < 8 && r() * 8 >= n) { this.spell = 2; this.castT = VEX_CAST; return; }
      this.vexCd = 40;
    }
    if (this.fangCd === 0) { this.spell = 1; this.castT = FANG_CAST; return; }
    // AvoidEntityGoal(Player, 8 blocks)
    if (this.d2(ctx) < 64) {
      if (!this.navAway(ctx, p.x, p.z, 1, 3, true)) this.setMove(this.x - p.x, this.z - p.z, 1);
    } else this.faceToward(p.x, p.z);
  }
  loot(rnd: () => number, byPlayer = false): Stack[] {
    const out = [stackOf(I.TOTEM_OF_UNDYING, 1)];
    if (byPlayer && rnd() < 0.5) out.push(stackOf(I.EMERALD, 1));
    return out;
  }
}

// --------------------------------------------------------------------- vex

export class Vex extends Foe {
  /** Ticks until it starts to die (0 = unlimited: spawn-egg vexes). */
  lifeTicks = 0;
  ownerId = 0;
  private charging = false;
  private gx = 0; private gy = 0; private gz = 0;
  private idle = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'vex', x, y, z, { width: 0.4, height: 0.8, eye: 0.52, health: 14, speed: 0.7 }, rnd);
    this.heldItem = I.IRON_SWORD;
    this.noFallDamage = true;
    this.gx = x; this.gy = y; this.gz = z;
  }
  xpValue(): number { return 3; }
  protected override floats(): boolean { return false; }
  /** Flies through blocks: moves itself, leaves nothing for the collision pass. */
  private fly(tx: number, ty: number, tz: number, sp: number): number {
    const dx = tx - this.x, dy = ty - this.y, dz = tz - this.z;
    const d = Math.hypot(dx, dy, dz);
    if (d > 1e-3) { const s = Math.min(sp, d) / d; this.x += dx * s; this.y = Math.max(1, this.y + dy * s); this.z += dz * s; }
    return d;
  }
  protected ai(ctx: MobCtx): void {
    const r = ctx.host.rnd, p = ctx.host.player;
    this.vx = 0; this.vy = 0; this.vz = 0;
    this.onGround = false;
    this.fallDistance = 0;
    if (this.attackCd > 0) this.attackCd--;
    if (this.lifeTicks > 0 && --this.lifeTicks === 0) this.lifeTicks = -1;
    if (this.lifeTicks < 0 && this.phase(ctx, 20)) { this.hurt(1); if (!this.alive) return; }
    if (ctx.targetable() && this.d2(ctx) < 24 * 24) {
      this.faceToward(p.x, p.z);
      this.lookAt(p.x, p.eyeY, p.z);
      if (!this.charging && this.attackCd === 0 && r() < 1 / 7) this.charging = true;
      if (this.charging) {
        this.aggressive = true;
        const d = this.fly(p.x, p.y + p.height * 0.6, p.z, 0.3);
        if (d < 0.9) { this.charging = false; this.attackCd = 30; this.swingTicks = 1; ctx.hurtPlayer(this, 9, 0.3); }
        return;
      }
      // hover around the target between charges
      if (--this.idle <= 0) { this.idle = 20 + ri(r, 0, 20); this.gx = p.x + ri(r, -5, 5); this.gy = p.y + 1 + r() * 4; this.gz = p.z + ri(r, -5, 5); }
    } else {
      this.charging = false;
      if (--this.idle <= 0) { this.idle = 40 + ri(r, 0, 60); this.gx = this.x + ri(r, -7, 7); this.gy = this.y + ri(r, -3, 3); this.gz = this.z + ri(r, -7, 7); }
      this.faceToward(this.gx, this.gz);
    }
    this.fly(this.gx, this.gy, this.gz, 0.1);
  }
  override tick(ctx: MobCtx): void {
    super.tick(ctx);
    // travel() adds gravity after ai; a vex has none
    if (!this.dying) { this.vx = 0; this.vy = 0; this.vz = 0; }
  }
  loot(): Stack[] { return []; }
}

// ----------------------------------------------------------------- ravager

export class Ravager extends Foe {
  /** The pose rides in the replicated record so clients draw it (server mode). */
  saveExtra(): unknown { return [this.color]; }
  loadExtra(d: unknown): void { if (Array.isArray(d)) this.color = Number(d[0]) | 0; }
  roarT = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'ravager', x, y, z, { width: 1.95, height: 2.2, eye: 1.8, health: 100, speed: 0.3 }, rnd);
    this.knockbackResist = 0.75;
    this.stepHeight = 1;
  }
  xpValue(): number { return 20; }
  override onHurtBy(_fx: number, _fz: number, rnd: () => number): void {
    this.retaliate = 200;
    if (this.roarT === 0 && rnd() < 0.1) this.roarT = 20;
  }
  /** Leaves and crops in its way break (vanilla Ravager.aiStep). */
  private trample(ctx: MobCtx): void {
    const set = ctx.host.setBlock;
    if (!set || !this.collidedH) return;
    const x0 = Math.floor(this.x - 1.2), x1 = Math.floor(this.x + 1.2), z0 = Math.floor(this.z - 1.2), z1 = Math.floor(this.z + 1.2);
    for (let y = Math.floor(this.y); y <= Math.floor(this.y + 2.2); y++) for (let z = z0; z <= z1; z++) for (let x = x0; x <= x1; x++) {
      const id = this.w.getBlock(x, y, z);
      if (id !== AIR && /Leaves|Wheat|Carrots|Potatoes|Beetroots/.test(BLOCKS[id]?.name ?? '')) set(x, y, z, AIR);
    }
  }
  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    this.color = this.roarT > 0 ? 1 : 0;
    if (this.roarT > 0) {
      // roar: 6 damage and a shove to whoever is within 4 blocks
      if (--this.roarT === 10 && ctx.targetable() && this.d2(ctx) < 16) ctx.hurtPlayer(this, 6, 1.5);
      if (this.roarT === 10) ctx.host.effect('poof', this.x, this.y + 1.5, this.z);
      return;
    }
    if (this.phase(ctx, 5)) this.trample(ctx);
    if (this.sees(ctx, 32)) this.chaseHit(ctx, 12, 1, 1.5);
    else { this.stroll(ctx, 0.4, 3, false); this.lookAtNearPlayer(ctx); }
  }
  loot(): Stack[] { return [stackOf(I.SADDLE, 1)]; }
}

// ------------------------------------------------------------------ zoglin

export class Zoglin extends Foe {
  private prey: Mob | null = null;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'zoglin', x, y, z, { width: 1.3965, height: 1.4, eye: 0.9, health: 40, speed: 0.3 }, rnd);
    this.knockbackResist = 0.6;
  }
  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    const r = ctx.host.rnd;
    if (this.sees(ctx, 16)) {
      if (this.chaseHit(ctx, ri(r, 3, 8), 1, 0.6)) netherHooks.toss?.(0.5);
      return;
    }
    // everything else that lives, creepers and its own kind aside
    if (this.phase(ctx, 20) || (this.prey && !this.prey.alive)) {
      this.prey = nearestMob(this, 12, (m) => m.type !== 'zoglin' && m.type !== 'creeper' && m.type !== 'shulker_bullet' && m.type !== 'evoker_fangs' && m.type !== 'vex');
    }
    const t = this.prey;
    if (t && t.alive) {
      this.aggressive = true;
      this.faceToward(t.x, t.z);
      const reach = (this.width + t.width) / 2 + 0.6;
      if ((t.x - this.x) ** 2 + (t.z - this.z) ** 2 < reach * reach && Math.abs(t.y - this.y) < 1.5) {
        if (this.attackCd === 0) { this.attackCd = 20; this.swingTicks = 1; if (mobs3Hooks.hurtMob(t, ri(r, 3, 8), this, 0.6)) t.vy = Math.max(t.vy, 0.4); }
      } else this.steerTo(ctx, t.x, t.y, t.z, 1, 3, false);
      return;
    }
    this.stroll(ctx, 0.6, 3, false);
  }
  loot(rnd: () => number): Stack[] { return [stackOf(I.ROTTEN_FLESH, ri(rnd, 1, 3))]; }
}

// ------------------------------------------------------------ piglin brute

export class PiglinBrute extends Foe {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'piglin_brute', x, y, z, { width: 0.6, height: 1.95, eye: 1.79, health: 50, speed: 0.35 }, rnd);
    this.heldItem = I.GOLDEN_AXE;
    this.persistent = true;
  }
  xpValue(): number { return 20; }
  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.sees(ctx, 16)) {
      // easy 7.5 / normal 13 / hard 19.5 with the axe
      const d = ctx.host.difficulty?.() ?? 'normal';
      this.chaseHit(ctx, d === 'easy' ? 7.5 : d === 'hard' ? 19.5 : 13, 1, 0.4);
    } else { this.stroll(ctx, 0.6, 3, false); this.lookAtNearPlayer(ctx); }
  }
  loot(rnd: () => number, byPlayer = false): Stack[] {
    return byPlayer && rnd() < 0.085 ? [{ id: I.GOLDEN_AXE, count: 1, dmg: Math.floor(rnd() * 28) }] : [];
  }
}
